import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAccountContext } from './account-provider';
import { GunNodeKey, MigrationStatus, TGunUserNode } from '@/types';
import { addToUserDDocCacheForGun } from '@/db/queries';
import { getRawDdocOnGunById, waitForGunData } from '@/utils/gun-utils';
import { isValidUUID } from '@/utils/helper';
import { GUN_ENV_NAMESPACE_KEY } from '@/utils/constants';
import { isNullish } from '@/utils/gun-utils';
import { KeyStoreInstance } from '@/utils/identity-utils/key-store';
import { decryptDdocItem } from '@/utils/worker-utils/utils';

interface IGunStorageContext {
  isGunInitialized: boolean;
}

const GunStorageContext = React.createContext<IGunStorageContext>({
  isGunInitialized: false,
});

export const useGunStorageContext = () => {
  return React.useContext(GunStorageContext);
};

export const GunStorageProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { userNode, walletAddress } = useAccountContext();
  const [isGunInitialized, setIsGunInitialized] = useState(false);

  const decryptCommentKeyFromGun = async (
    commentKey: string,
    userNode: TGunUserNode
  ) => {
    if (!commentKey) return commentKey;
    // @ts-ignore
    const result = await decryptDdocItem(
      { commentKey },
      // @ts-ignore
      userNode._.sea.epriv
    );
    return result.commentKey;
  };

  const getUserDdocListOnGun = useCallback(
    async (userNode: TGunUserNode) => {
      if (!userNode || !walletAddress) return [];

      try {
        // Get Gun data for documents
        const docsOnGun = await waitForGunData(
          userNode
            .get(GUN_ENV_NAMESPACE_KEY)
            .get(GunNodeKey.Ddoc)
            .get(GunNodeKey.Files)
        );

        // Process and map documents
        const allDocs = await Promise.all(
          Object.entries(docsOnGun)
            .filter(
              ([key]) => key !== '_' && !isNullish(key) && isValidUUID(key)
            )
            .map(async ([key]) => {
              const docItem = await getRawDdocOnGunById(userNode, key);

              if (!docItem || docItem.isDeleted || docItem.markedForDeletion) {
                return null;
              }

              const cacheItem = {
                ddocId: key,
                publishStatus: docItem.publishStatus,
                createdAt: docItem.createdAt || Date.now(),
                last_updated: docItem.last_updated,
                portalAddress: docItem.portalAddress,
                title: docItem.title,
                onChainFileId: docItem.onChainFileId,
                address: walletAddress,
                needToDecryptTitle: false,
                linkKey: docItem.linkKey,
                linkKeyNonce: docItem.linkKeyNonce,
                ownerLock: null,
                portalLock: null,
                commentKey: await decryptCommentKeyFromGun(
                  docItem.commentKey,
                  userNode
                ),
                migrationStatus: MigrationStatus.Pending,
                markedForDeletion: !isNullish(docItem.markedForDeletion)
                  ? docItem.markedForDeletion
                  : false,
              };

              if (walletAddress) {
                await addToUserDDocCacheForGun(
                  walletAddress,
                  cacheItem,
                  userNode
                );
              }

              return cacheItem;
            })
        );

        return allDocs.filter((doc) => doc !== null);
      } catch (err) {
        console.error('Error fetching user documents:', err);
        return [];
      }
    },
    [walletAddress]
  );

  useEffect(() => {
    if (userNode && KeyStoreInstance.isLegacyUser) {
      getUserDdocListOnGun(userNode).then(() => {
        setIsGunInitialized(true);
      });
    } else {
      setIsGunInitialized(true);
    }
  }, [getUserDdocListOnGun, userNode]);

  const contextValue = useMemo(() => {
    return {
      isGunInitialized,
    };
  }, [isGunInitialized]);

  return (
    <GunStorageContext.Provider value={contextValue}>
      {children}
    </GunStorageContext.Provider>
  );
};
